import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, CalendarDays, Home as HomeIcon, Lock, Check, MessageSquare, TrendingUp, ClipboardList, Printer } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Stars } from "@/components/shared";
import { ResaleEditor } from "@/components/ResaleEditor";
import { ResaleSection } from "@/components/ResaleSection";
import { NearbyEditor } from "@/components/NearbyEditor";
import { WishMatchEditor } from "@/components/WishMatch";
import { MapPin, Target } from "lucide-react";

const emptyTour = { title: "", address: "", price: "", sqft: "", tour_date: "", link: "", image_url: "", notes: "", estimated_value: "", estimate_note: "" };

const VERDICT = {
  love: { label: "Love it", cls: "bg-status-complete/15 text-status-complete" },
  maybe: { label: "Maybe", cls: "bg-status-warning/15 text-status-warning" },
  pass: { label: "Pass", cls: "bg-destructive/15 text-destructive" },
};

const detailGet = (t, re) => {
  const e = Object.entries(t.details || {}).find(([k]) => re.test(k));
  return e && String(e[1] ?? "").trim() ? e[1] : null;
};
const basicsMissing = (t) => {
  const miss = [];
  if (t.price == null) miss.push("Price");
  if (!detailGet(t, /hoa|maintenance/i)) miss.push("HOA / maintenance");
  if (!detailGet(t, /tax/i)) miss.push("Property tax");
  if (!detailGet(t, /bed/i)) miss.push("Beds");
  if (!detailGet(t, /bath/i)) miss.push("Baths");
  if (!String(t.sqft || "").trim()) miss.push("Sq ft");
  if (!detailGet(t, /park/i)) miss.push("Parking");
  if (!detailGet(t, /\bac\b|air.?condition/i)) miss.push("AC");
  if (!detailGet(t, /tenure|lease|fee simple/i)) miss.push("Tenure");
  if (!(t.criteria_meets?.length || t.criteria_not_meets?.length)) miss.push("Criteria match");
  if (!String(t.tour_date || "").trim()) miss.push("Showing date/time");
  return miss;
};
const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
function openPrint(title, html) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>${esc(title)}</title><style>body{font-family:system-ui,-apple-system,sans-serif;padding:32px;color:#1a1a1a;max-width:700px}h1{font-size:20px}h2{font-size:15px;margin-top:18px}ul{padding-left:18px}li{margin:5px 0}.muted{color:#666;font-size:12px;margin-top:16px}</style></head><body>${html}<script>window.onload=function(){window.print()}<\/script></body></html>`);
  w.document.close();
}
function printStillGetting(t) {
  const open = (t.prep_checklist || []).filter((it) => !it.done);
  const items = open.length ? `<ul>${open.map((it) => `<li>${esc(it.label)}</li>`).join("")}</ul>` : `<p class="muted">Nothing outstanding right now.</p>`;
  openPrint("What I'm still getting for you", `<h1>What I'm still getting for you</h1><h2>${esc(t.title) || "Home"}</h2>${items}<p class="muted">Items your agent is still gathering before an offer. Nothing here is a guarantee.</p>`);
}
function printCheat(t) {
  const body = (t.cheat_sheet || "").trim() ? `<p>${esc(t.cheat_sheet).replace(/\n/g, "<br>")}</p>` : `<p class="muted">No cheat sheet yet.</p>`;
  openPrint("Agent cheat sheet", `<h1>Agent cheat sheet — ${esc(t.title) || "Home"}</h1>${body}<p class="muted">Agent-only. Not shared with the buyer.</p>`);
}

export function AgentTours({ clientId, tours = [], criteria = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tour, setTour] = useState(emptyTour);
  const [comp, setComp] = useState({});
  const invalidate = () => qc.invalidateQueries({ queryKey: ["client", clientId] });

  const addTour = useMutation({
    mutationFn: async () => (await api.post(`/agent/clients/${clientId}/tours`, {
      ...tour, price: tour.price ? Number(tour.price) : null, estimated_value: tour.estimated_value ? Number(tour.estimated_value) : null,
    })).data,
    onSuccess: () => { invalidate(); setOpen(false); setTour(emptyTour); toast.success("Tour added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delTour = useMutation({ mutationFn: async (id) => (await api.delete(`/agent/tours/${id}`)).data, onSuccess: () => { invalidate(); toast.success("Tour removed"); } });
  const addComp = useMutation({
    mutationFn: async ({ tourId, data }) => (await api.post(`/agent/tours/${tourId}/comps`, { ...data, sold_price: data.sold_price ? Number(data.sold_price) : null })).data,
    onSuccess: () => { invalidate(); setComp({}); toast.success("Comp added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delComp = useMutation({ mutationFn: async ({ tourId, compId }) => (await api.delete(`/agent/tours/${tourId}/comps/${compId}`)).data, onSuccess: invalidate });
  const setToured = useMutation({
    mutationFn: async ({ id, toured }) => (await api.put(`/agent/tours/${id}/toured`, { toured })).data,
    onSuccess: () => { invalidate(); toast.success("Compare board updated"); },
  });
  const [prep, setPrep] = useState({});
  const addPrep = useMutation({
    mutationFn: async ({ tourId, label }) => (await api.post(`/agent/tours/${tourId}/checklist`, { label })).data,
    onSuccess: (_, v) => { invalidate(); setPrep((p) => ({ ...p, [v.tourId]: "" })); },
  });
  const togglePrep = useMutation({
    mutationFn: async ({ tourId, itemId, done }) => (await api.put(`/agent/tours/${tourId}/checklist/${itemId}`, { done })).data,
    onSuccess: invalidate,
  });
  const setStage = useMutation({
    mutationFn: async ({ id, patch }) => (await api.put(`/agent/tours/${id}/stage`, patch)).data,
    onSuccess: () => invalidate(),
  });
  const saveCheat = useMutation({
    mutationFn: async ({ id, cheat_sheet }) => (await api.put(`/agent/tours/${id}/cheat-sheet`, { cheat_sheet })).data,
    onSuccess: () => { invalidate(); toast.success("Cheat sheet saved"); },
  });
  const touredState = (t) => typeof t.toured === "boolean" ? t.toured : /toured|shown|open house/i.test(`${t.tour_date || ""} ${t.details?.Status || ""}`);

  const setC = (tid, k) => (e) => setComp({ ...comp, [tid]: { ...(comp[tid] || {}), [k]: e.target.value } });

  return (
    <div className="space-y-4">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="add-tour-btn"><Plus className="h-4 w-4" /> Add tour property</Button></DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a tour property</DialogTitle>
            <DialogDescription>A home you're planning to tour with this client. Add comps after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name / title *</Label><Input value={tour.title} onChange={(e) => setTour({ ...tour, title: e.target.value })} data-testid="tour-title-input" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={tour.address} onChange={(e) => setTour({ ...tour, address: e.target.value })} data-testid="tour-address-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>List price</Label><Input type="number" value={tour.price} onChange={(e) => setTour({ ...tour, price: e.target.value })} data-testid="tour-price-input" /></div>
              <div className="space-y-1.5"><Label>Sqft</Label><Input value={tour.sqft} onChange={(e) => setTour({ ...tour, sqft: e.target.value })} data-testid="tour-sqft-input" /></div>
              <div className="space-y-1.5"><Label>Tour date</Label><Input type="date" value={tour.tour_date} onChange={(e) => setTour({ ...tour, tour_date: e.target.value })} data-testid="tour-date-input" /></div>
              <div className="space-y-1.5"><Label>Est. value</Label><Input type="number" value={tour.estimated_value} onChange={(e) => setTour({ ...tour, estimated_value: e.target.value })} data-testid="tour-estvalue-input" /></div>
              <div className="space-y-1.5"><Label>Image URL</Label><Input value={tour.image_url} onChange={(e) => setTour({ ...tour, image_url: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Listing link</Label><Input value={tour.link} onChange={(e) => setTour({ ...tour, link: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Estimate note</Label><Input value={tour.estimate_note} onChange={(e) => setTour({ ...tour, estimate_note: e.target.value })} placeholder="e.g. based on recent nearby sales" /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={tour.notes} onChange={(e) => setTour({ ...tour, notes: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter><Button onClick={() => addTour.mutate()} disabled={!tour.title || addTour.isPending} data-testid="tour-save-btn" className="rounded-full">{addTour.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save tour"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {tours.length === 0 ? (
        <Card className="p-8 rounded-2xl text-center text-muted-foreground">No tour properties yet. Add the homes you'll tour with this client.</Card>
      ) : tours.map((t) => (
        <Card key={t.id} className="p-5 rounded-2xl" data-testid={`agent-tour-${t.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">{t.image_url ? <img src={t.image_url} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <HomeIcon className="h-4 w-4 text-muted-foreground" />}</div>
              <div>
                <p className="font-semibold">{t.title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-2">{t.address || "—"} {t.tour_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {t.tour_date}</span>}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant={touredState(t) ? "default" : "outline"} size="sm" className="rounded-full gap-1 h-8" onClick={() => setToured.mutate({ id: t.id, toured: !touredState(t) })} data-testid={`tour-toured-toggle-${t.id}`}>
                <Check className="h-3.5 w-3.5" /> {touredState(t) ? "Toured · on Compare" : "Mark toured"}
              </Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => delTour.mutate(t.id)} data-testid={`tour-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`stage-controls-${t.id}`}>
            <Button variant={t.ready_for_tour ? "default" : "outline"} size="sm" className={`rounded-full gap-1 h-8 ${t.ready_for_tour ? "bg-status-complete text-white hover:bg-status-complete/90" : ""}`} onClick={() => setStage.mutate({ id: t.id, patch: { ready_for_tour: !t.ready_for_tour } })} data-testid={`ready-toggle-${t.id}`}>
              <Check className="h-3.5 w-3.5" /> {t.ready_for_tour ? "Ready for Tour" : "Mark Ready for Tour"}
            </Button>
            <Button variant={t.offer_prep ? "default" : "outline"} size="sm" className="rounded-full gap-1 h-8" onClick={() => setStage.mutate({ id: t.id, patch: { offer_prep: !t.offer_prep } })} data-testid={`offer-toggle-${t.id}`}>
              <ClipboardList className="h-3.5 w-3.5" /> {t.offer_prep ? "Preparing an offer · on" : "Start offer prep"}
            </Button>
          </div>
          {basicsMissing(t).length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground" data-testid={`basics-missing-${t.id}`}>
              <span className="font-medium text-foreground">Tour basics still missing:</span> {basicsMissing(t).join(", ")}.
              <span className="block text-[11px]">You can still mark it Ready for Tour — this is just a reminder.</span>
            </div>
          )}

          {(t.criteria_meets?.length > 0 || t.criteria_not_meets?.length > 0 || t.agent_verify?.length > 0) && (
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {t.criteria_meets?.length > 0 && (
                <div className="p-3 rounded-xl bg-status-complete/10 border border-status-complete/20">
                  <p className="text-xs font-semibold text-status-complete mb-1">Meets</p>
                  <ul className="space-y-0.5">{t.criteria_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                </div>
              )}
              {t.criteria_not_meets?.length > 0 && (
                <div className="p-3 rounded-xl bg-status-warning/10 border border-status-warning/20">
                  <p className="text-xs font-semibold text-status-warning mb-1">Doesn't meet</p>
                  <ul className="space-y-0.5">{t.criteria_not_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                </div>
              )}
              {t.agent_verify?.length > 0 && (
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20" data-testid={`agent-verify-${t.id}`}>
                  <p className="text-xs font-semibold text-primary flex items-center gap-1 mb-1"><Lock className="h-3 w-3" /> To verify · agent only</p>
                  <ul className="space-y-0.5">{t.agent_verify.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                  <p className="text-[10px] text-muted-foreground mt-1.5">Not shown to the buyer.</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 border-t border-border pt-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Comparable sales ({t.comps?.length || 0})</p>
            {(t.comps || []).map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-sm py-1">
                <span className="flex-1 truncate">{c.address}</span>
                <span className="text-muted-foreground">{c.sold_price ? "$" + Number(c.sold_price).toLocaleString() : "—"}</span>
                <span className="text-xs text-muted-foreground">{c.sold_date}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delComp.mutate({ tourId: t.id, compId: c.id })} data-testid={`comp-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 mt-2">
              <Input placeholder="Comp address" value={comp[t.id]?.address || ""} onChange={setC(t.id, "address")} className="h-9 flex-1 min-w-[140px]" data-testid={`comp-address-${t.id}`} />
              <Input placeholder="Sold $" type="number" value={comp[t.id]?.sold_price || ""} onChange={setC(t.id, "sold_price")} className="h-9 w-24" data-testid={`comp-price-${t.id}`} />
              <Input placeholder="Date" type="date" value={comp[t.id]?.sold_date || ""} onChange={setC(t.id, "sold_date")} className="h-9 w-36" />
              <Input placeholder="Sqft" value={comp[t.id]?.sqft || ""} onChange={setC(t.id, "sqft")} className="h-9 w-20" />
              <Button size="sm" className="h-9 rounded-full" disabled={!comp[t.id]?.address || addComp.isPending} onClick={() => addComp.mutate({ tourId: t.id, data: comp[t.id] })} data-testid={`comp-add-${t.id}`}>Add comp</Button>
            </div>
          </div>

          {t.feedback_by_user && Object.keys(t.feedback_by_user).length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> Buyer feedback</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(t.feedback_by_user).map(([uid, fb]) => (
                  <div key={uid} className="p-3 rounded-xl bg-secondary" data-testid={`buyer-feedback-${uid}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{fb.name || "Buyer"}</p>
                      {fb.verdict && VERDICT[fb.verdict] && <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${VERDICT[fb.verdict].cls}`}>{VERDICT[fb.verdict].label}</span>}
                    </div>
                    {fb.overall ? <div className="mt-1"><Stars value={fb.overall} /></div> : null}
                    {fb.criteria && Object.entries(fb.criteria).filter(([, v]) => v > 0).length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        {Object.entries(fb.criteria).filter(([, v]) => v > 0).map(([c, v]) => (
                          <div key={c} className="flex justify-between text-xs"><span className="text-muted-foreground">{c}</span><span className="font-medium">{v}/5</span></div>
                        ))}
                      </div>
                    )}
                    {fb.likes && <p className="text-xs mt-2"><span className="font-semibold text-status-complete">Likes:</span> {fb.likes}</p>}
                    {fb.dislikes && <p className="text-xs mt-1"><span className="font-semibold text-status-warning">Dislikes:</span> {fb.dislikes}</p>}
                    <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><Lock className="h-3 w-3" /> Their private notes stay private — not shown to you.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {t.offer_prep && (
            <div className="mt-4 border-t border-border pt-3" data-testid={`offer-prep-${t.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Offer prep checklist</p>
                <Button variant="outline" size="sm" className="rounded-full h-7 gap-1 text-xs" onClick={() => printStillGetting(t)} data-testid={`print-still-${t.id}`}><Printer className="h-3 w-3" /> Print "still getting"</Button>
              </div>
              {(t.prep_checklist || []).map((it) => (
                <label key={it.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`prep-item-${it.id}`}>
                  <input type="checkbox" checked={!!it.done} onChange={() => togglePrep.mutate({ tourId: t.id, itemId: it.id, done: !it.done })} className="h-4 w-4 accent-primary" data-testid={`prep-toggle-${it.id}`} />
                  <span className={it.done ? "line-through text-muted-foreground" : ""}>{it.label}</span>
                </label>
              ))}
              <div className="flex gap-2 mt-2">
                <Input placeholder="Add offer item (e.g. HOA docs, reserves, disclosures)" value={prep[t.id] || ""} onChange={(e) => setPrep({ ...prep, [t.id]: e.target.value })} className="h-9 flex-1" data-testid={`prep-input-${t.id}`} />
                <Button size="sm" className="h-9 rounded-full" disabled={!prep[t.id] || addPrep.isPending} onClick={() => addPrep.mutate({ tourId: t.id, label: prep[t.id] })} data-testid={`prep-add-${t.id}`}>Add</Button>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary flex items-center gap-1"><Lock className="h-3 w-3" /> Agent cheat sheet · agent only</p>
                  <Button variant="outline" size="sm" className="rounded-full h-7 gap-1 text-xs" onClick={() => printCheat(t)} data-testid={`print-cheat-${t.id}`}><Printer className="h-3 w-3" /> Print</Button>
                </div>
                <Textarea defaultValue={t.cheat_sheet || ""} placeholder="Tour notes & talking points — never shown to the buyer." className="rounded-xl"
                  onBlur={(e) => { if (e.target.value !== (t.cheat_sheet || "")) saveCheat.mutate({ id: t.id, cheat_sheet: e.target.value }); }}
                  data-testid={`cheat-input-${t.id}`} />
                <p className="text-[10px] text-muted-foreground mt-1">Not shown to the buyer.</p>
              </div>
            </div>
          )}

          <Accordion type="single" collapsible className="mt-4">
            <AccordionItem value="resale" className="border border-forest/30 rounded-xl bg-forest/5 px-3">
              <AccordionTrigger className="text-sm py-3 hover:no-underline" data-testid={`resale-editor-toggle-${t.id}`}>
                <span className="flex items-center gap-2 font-semibold text-forest">
                  <TrendingUp className="h-4 w-4" /> Comps &amp; Resale for this property
                  <span className="text-xs font-normal text-muted-foreground">
                    {t.resale?.sold?.length ? `· ${t.resale.sold.length} sold comp${t.resale.sold.length > 1 ? "s" : ""} added` : "· add recent sold sales — buyers see them on this home"}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ResaleEditor tourId={t.id} resale={t.resale} onSaved={invalidate} />
                {(t.resale && (t.resale.sold?.length || t.resale.summary || t.resale.est_resale_range)) ? (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">What the buyer sees</p>
                    <ResaleSection resale={t.resale} />
                  </div>
                ) : null}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="wish" className="border border-border rounded-xl px-3 mt-2">
              <AccordionTrigger className="text-sm py-3 hover:no-underline" data-testid={`wish-editor-toggle-${t.id}`}>
                <span className="flex items-center gap-2 font-semibold"><Target className="h-4 w-4 text-primary" /> Wish list match score
                  <span className="text-xs font-normal text-muted-foreground">{Array.isArray(t.wish_matches) ? `· ${t.wish_matches.length} ticked` : "· not scored yet"}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent><WishMatchEditor tour={t} criteria={criteria} onSaved={invalidate} /></AccordionContent>
            </AccordionItem>
            <AccordionItem value="nearby" className="border border-border rounded-xl px-3 mt-2">
              <AccordionTrigger className="text-sm py-3 hover:no-underline" data-testid={`nearby-editor-toggle-${t.id}`}>
                <span className="flex items-center gap-2 font-semibold"><MapPin className="h-4 w-4 text-forest" /> What's Nearby
                  <span className="text-xs font-normal text-muted-foreground">· {(t.nearby?.items?.length || 0)} places, {(t.nearby?.drives?.length || 0)} drive times</span>
                </span>
              </AccordionTrigger>
              <AccordionContent><NearbyEditor tour={t} onSaved={invalidate} /></AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      ))}
      <p className="text-xs text-muted-foreground">Comps and estimated values are shown to the buyer with an "estimate only" disclaimer.</p>
    </div>
  );
}
